/**
 *
 * ResourceItemSelector
 *
 */

/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import AssessmentItemSmall from 'components/AssessmentItemSmall';
import FaqItem from 'components/FaqItemSmall';
import ResourceItem from 'components/ResourceItemSmall';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { SeriesItem } from 'components/SeriesItem';
import { getFormattedType, getFinalType } from './utils';

const ResourceItemSelector = ({
  item,
  itemIndex,
  listTitle,
  recommendation,
  expanded,
  isLazyList,
  personalizedCard,
  isPdf,
  grayscale,
  useClientImage,
  assessmentsDurationAltPosition,
  trackingPath,
  clientExclusiveAsResource = false,
  asLink = true,
  openLinkInNewTab = false,
  showFavoriteButton,
  renderFooter,
  customClasses,
}) => {
  const [siteCopy] = useSiteCopySelector(['tooltip']);

  if (_isEmpty(item)) return false;

  const isWhatHelps = item.parent === 'what-helps';
  const fromContentful = _has(item, 'fields');
  const fromContentfulGql = _has(item, '__typename');
  const finalFields = isWhatHelps
    ? typeof item.evidenceData.approach === 'boolean'
      ? {
          ...item.evidenceData,
          evidenceColor: item.iconColor,
          evidenceName:
            item.evidenceData.evidence ||
            _get(siteCopy, ['pageCopy', 'labels', 'Good Idea']),
        }
      : {
          ...item.evidenceData.approach.fields,
          evidenceColor: item.iconColor,
          evidenceName:
            item.evidenceData.evidence ||
            _get(siteCopy, ['pageCopy', 'labels', 'Good Idea']),
        }
    : fromContentful
    ? item.fields
    : item;
  const finalId = isWhatHelps
    ? _get(item, 'evidenceData.approach.sys.id', item.evidenceData.id)
    : fromContentful || fromContentfulGql
    ? item.sys.id
    : item.objectID;
  const finalType = getFinalType(
    item,
    isWhatHelps,
    fromContentful || fromContentfulGql,
    clientExclusiveAsResource,
  );

  const finalItem = {
    ...finalFields,
    id: finalId,
    podcastType: _get(finalFields, 'podcastType') || _get(finalFields, 'type'),
    type: finalType || 'News',
  };

  const formattedType = getFormattedType(finalType);

  const resourceItemCommonProps = {
    item: finalItem,
    type: formattedType,
    recommendation,
    itemIndex,
    listTitle,
    isLazyList,
    isPdf,
    grayscale,
    trackingPath,
    asLink,
    openLinkInNewTab,
    showFavoriteButton,
    renderFooter,
    customClasses,
  };

  if (finalType === 'Assessments' || finalType === 'assessments')
    return (
      <AssessmentItemSmall
        item={finalItem}
        expanded={expanded}
        listTitle={listTitle}
        grayscale={grayscale}
        useClientImage={useClientImage}
        durationAltPosition={assessmentsDurationAltPosition}
        trackingPath={trackingPath}
      />
    );
  if (finalType === 'Videos' || finalType === 'video')
    return <ResourceItem {...resourceItemCommonProps} route="/videos" />;
  if (finalType === 'Apps' || finalType === 'application')
    return <ResourceItem {...resourceItemCommonProps} route="/apps" />;
  if (
    finalType === 'Links' ||
    finalType === 'link' ||
    finalType === 'Client Exclusive'
  )
    return <ResourceItem {...resourceItemCommonProps} />;
  if (
    finalType === 'Podcasts' ||
    finalType === 'podcast' ||
    finalType === 'Episode' ||
    finalType === 'episode'
  )
    return (
      <ResourceItem
        {...resourceItemCommonProps}
        route="/podcasts"
        personalizedCard={personalizedCard}
      />
    );
  if (finalType === 'People' || finalType === 'person') {
    const {
      recommendation: _recommendation,
      ...rest
    } = resourceItemCommonProps;

    return <ResourceItem {...rest} type="person" route="/people" />;
  }
  if (finalType === 'Books' || finalType === 'book')
    return <ResourceItem {...resourceItemCommonProps} route="/books" />;
  if (
    finalType === 'Articles' ||
    finalType === 'activity' ||
    finalType === 'News'
  )
    return (
      <ResourceItem
        {...resourceItemCommonProps}
        route="/articles"
        personalizedCard={personalizedCard}
      />
    );
  if (finalType === 'Insights' || finalType === 'Blogs')
    return <ResourceItem {...resourceItemCommonProps} route="/insights" />;
  if (finalType === 'FAQ' || finalType === 'source')
    return (
      <FaqItem
        item={finalItem}
        recommendation={recommendation}
        index={itemIndex}
        listTitle={listTitle}
        expanded={expanded}
        grayscale={grayscale}
        trackingPath={trackingPath}
        asLink={asLink}
      />
    );
  if (finalType === 'Organizations' || finalType === 'organization')
    return <ResourceItem {...resourceItemCommonProps} route="/organizations" />;
  if (finalType === 'Online Programs' || finalType === 'onlineProgram')
    return (
      <ResourceItem
        {...resourceItemCommonProps}
        type="program"
        route="/programs"
      />
    );
  if (finalType === 'Topics' || finalType === 'topics')
    return (
      <ResourceItem
        {...resourceItemCommonProps}
        type="topic"
        route="/topics"
        evidence={isWhatHelps}
        personalizedCard={personalizedCard}
      />
    );
  if (finalType === 'Services' || finalType === 'services')
    return (
      <ResourceItem
        {...resourceItemCommonProps}
        type="service"
        route="/services"
      />
    );
  if (finalType === 'Lists' || finalType === 'lists' || finalType === 'list')
    return (
      <ResourceItem
        {...resourceItemCommonProps}
        type="list"
        route="/lists"
        personalizedCard={personalizedCard}
      />
    );
  if (finalType === 'series' || finalType === 'Series') {
    return (
      <SeriesItem
        isInCarousel={itemIndex !== undefined}
        item={finalItem}
        trackingPath={trackingPath}
      />
    );
  }
  return null;
};

ResourceItemSelector.propTypes = {
  item: PropTypes.object,
  recommendation: PropTypes.bool,
  itemIndex: PropTypes.number,
  listTitle: PropTypes.string,
  expanded: PropTypes.bool,
  isLazyList: PropTypes.bool,
  isPdf: PropTypes.bool,
  grayscale: PropTypes.bool,
  useClientImage: PropTypes.bool,
  customClasses: PropTypes.objectOf(PropTypes.string),
  personalizedCard: PropTypes.bool,
  openLinkInNewTab: PropTypes.bool,
  showFavoriteButton: PropTypes.bool,
  renderFooter: PropTypes.func,
};

ResourceItemSelector.defaultProps = {
  recommendation: false,
};

export default ResourceItemSelector;
